<script setup>
import { reactive, computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';
import { CAMPAIGN_TYPES } from 'shared/constants/campaign.js';

import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import LiveCampaignPresetsAPI from 'dashboard/api/liveCampaignPresets';

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();

const formState = {
  uiFlags: useMapGetter('campaigns/getUIFlags'),
  labels: useMapGetter('labels/getLabels'),
  inboxes: useMapGetter('inboxes/getWhatsAppInboxes'),
};

const initialState = {
  title: '',
  message: '',
  inboxId: null,
  selectedAudience: [],
};

const state = reactive({ ...initialState });

// v87: imagem opcional (vai junto do texto como legenda)
const imageFile = ref(null);
const imageInput = ref(null);
const onImageSelected = event => {
  const [file] = event.target.files || [];
  imageFile.value = file || null;
  usedPresetImage.value = false;
};

// v87: modelos salvos — usar no inicio, salvar no fim
const presets = ref([]);
const selectedPresetId = ref(null);
const usedPresetImage = ref(false);
const saveAsPreset = ref(false);
const presetName = ref('');

const presetOptions = computed(() =>
  presets.value.map(p => ({ value: p.id, label: p.name }))
);

const applyPreset = presetId => {
  const preset = presets.value.find(p => p.id === presetId);
  if (!preset) return;
  state.title = preset.title || preset.name;
  state.message = preset.message;
  state.inboxId = preset.inbox_id;
  state.selectedAudience = (preset.audience || [])
    .filter(a => a.type === 'Label')
    .map(a => a.id);
  imageFile.value = null;
  usedPresetImage.value = !!preset.has_image;
};

onMounted(async () => {
  try {
    const { data } = await LiveCampaignPresetsAPI.list();
    presets.value = data.data || [];
  } catch (e) {
    presets.value = [];
  }
});

const rules = {
  title: { required, minLength: minLength(1) },
  message: { required, minLength: minLength(1) },
  inboxId: { required },
  selectedAudience: { required },
};

const v$ = useVuelidate(rules, state);

const isCreating = computed(() => formState.uiFlags.value.isCreating);

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({
    value: item[valueKey],
    label: item[labelKey],
  })) ?? [];

const audienceList = computed(() =>
  mapToOptions(formState.labels.value, 'id', 'title')
);

const inboxOptions = computed(() =>
  mapToOptions(formState.inboxes.value, 'id', 'name')
);

const getErrorMessage = (field, errorKey) => {
  const baseKey = 'CAMPAIGN.LIVE_WHATSAPP.CREATE.FORM';
  return v$.value[field].$error ? t(`${baseKey}.${errorKey}.ERROR`) : '';
};

const formErrors = computed(() => ({
  title: getErrorMessage('title', 'TITLE'),
  message: getErrorMessage('message', 'MESSAGE'),
  inbox: getErrorMessage('inboxId', 'INBOX'),
  audience: getErrorMessage('selectedAudience', 'AUDIENCE'),
}));

const isSubmitDisabled = computed(() => v$.value.$invalid);

const resetState = () => {
  Object.assign(state, initialState);
};

const handleCancel = () => emit('cancel');

const prepareCampaignDetails = () => ({
  title: state.title,
  message: state.message,
  inbox_id: state.inboxId,
  campaign_type: CAMPAIGN_TYPES.LIVE_WHATSAPP,
  audience: state.selectedAudience?.map(id => ({
    id,
    type: 'Label',
  })),
  // v87: imagem (upload novo OU herdada do modelo salvo) + salvar modelo
  file: imageFile.value || undefined,
  preset_id: usedPresetImage.value ? selectedPresetId.value : undefined,
  save_as_preset: saveAsPreset.value || undefined,
  preset_name: saveAsPreset.value ? presetName.value.trim() || state.title : undefined,
});

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  emit('submit', prepareCampaignDetails());
  resetState();
  handleCancel();
};
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <div v-if="presetOptions.length" class="flex flex-col gap-1">
      <label class="mb-0.5 text-sm font-medium text-n-slate-12">
        Utilizar modelo salvo
      </label>
      <ComboBox
        v-model="selectedPresetId"
        :options="presetOptions"
        placeholder="Escolha um modelo (opcional)"
        class="[&>div>button]:bg-n-alpha-black2"
        @update:model-value="applyPreset"
      />
      <p v-if="usedPresetImage" class="text-xs text-n-teal-11">
        ✓ A imagem do modelo será enviada junto (suba outra pra substituir).
      </p>
    </div>

    <Input
      v-model="state.title"
      :label="t('CAMPAIGN.LIVE_WHATSAPP.CREATE.FORM.TITLE.LABEL')"
      :placeholder="t('CAMPAIGN.LIVE_WHATSAPP.CREATE.FORM.TITLE.PLACEHOLDER')"
      :message="formErrors.title"
      :message-type="formErrors.title ? 'error' : 'info'"
    />

    <TextArea
      v-model="state.message"
      :label="t('CAMPAIGN.LIVE_WHATSAPP.CREATE.FORM.MESSAGE.LABEL')"
      :placeholder="t('CAMPAIGN.LIVE_WHATSAPP.CREATE.FORM.MESSAGE.PLACEHOLDER')"
      :max-length="4096"
      custom-text-area-class="!ring-0 focus:!ring-0 !shadow-none focus:!shadow-none"
      show-character-count
      :message="formErrors.message"
      :message-type="formErrors.message ? 'error' : 'info'"
    />

    <div class="flex flex-col gap-1">
      <label for="inbox" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.LIVE_WHATSAPP.CREATE.FORM.INBOX.LABEL') }}
      </label>
      <ComboBox
        id="inbox"
        v-model="state.inboxId"
        :options="inboxOptions"
        :has-error="!!formErrors.inbox"
        :placeholder="t('CAMPAIGN.LIVE_WHATSAPP.CREATE.FORM.INBOX.PLACEHOLDER')"
        :message="formErrors.inbox"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
    </div>

    <div class="flex flex-col gap-1">
      <label for="audience" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.LIVE_WHATSAPP.CREATE.FORM.AUDIENCE.LABEL') }}
      </label>
      <TagMultiSelectComboBox
        v-model="state.selectedAudience"
        :options="audienceList"
        :label="t('CAMPAIGN.LIVE_WHATSAPP.CREATE.FORM.AUDIENCE.LABEL')"
        :placeholder="t('CAMPAIGN.LIVE_WHATSAPP.CREATE.FORM.AUDIENCE.PLACEHOLDER')"
        :has-error="!!formErrors.audience"
        :message="formErrors.audience"
        class="[&>div>button]:bg-n-alpha-black2"
      />
    </div>

    <div class="flex flex-col gap-1">
      <label class="mb-0.5 text-sm font-medium text-n-slate-12">
        Imagem (opcional — enviada junto com o texto)
      </label>
      <div class="flex items-center gap-2">
        <input
          ref="imageInput"
          type="file"
          accept="image/png,image/jpeg"
          class="text-sm text-n-slate-11"
          @change="onImageSelected"
        />
        <span v-if="imageFile" class="text-xs text-n-teal-11 truncate">
          ✓ {{ imageFile.name }}
        </span>
      </div>
    </div>

    <label class="flex items-start gap-2 text-sm text-n-slate-12 cursor-pointer">
      <input v-model="saveAsPreset" type="checkbox" class="mt-0.5" />
      <span>
        Salvar como modelo
        <span class="text-xs text-n-slate-11">
          (guarda mensagem, imagem, caixa e etiquetas pra reutilizar — o
          público é sempre recalculado na hora do disparo)
        </span>
      </span>
    </label>
    <Input
      v-if="saveAsPreset"
      v-model="presetName"
      label="Nome do modelo"
      :placeholder="state.title || 'ex: oferta_padrao'"
    />

    <div class="flex items-center justify-between w-full gap-3">
      <Button
        variant="faded"
        color="slate"
        type="button"
        :label="t('CAMPAIGN.LIVE_WHATSAPP.CREATE.FORM.BUTTONS.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        :label="t('CAMPAIGN.LIVE_WHATSAPP.CREATE.FORM.BUTTONS.CREATE')"
        class="w-full"
        type="submit"
        :is-loading="isCreating"
        :disabled="isCreating || isSubmitDisabled"
      />
    </div>
  </form>
</template>
